/**
 * Step 1 of the create-alias wizard: the username.
 *
 * Free aliases are handed a generated name with a reroll control and cannot type
 * their own; paid aliases get a free-form field. The server re-checks the shape,
 * the blocked-word list, and availability before the wizard advances, so this
 * component only renders the outcome.
 *
 * The reroll control sits in the input's own icon slot rather than beside the
 * field, so it stays centred on the input no matter what the label, hint, and
 * error lines above and below it are doing.
 */

import { useTranslation } from 'react-i18next';
import { USERNAME_MAX_LENGTH, type NameCheckRejectionCode } from '@adieuu/shared';
import { Input } from '../../components/Input';
import { Icon } from '../../icons/Icon';
import { Tooltip } from '../../components/Tooltip';

export interface CreateUsernameStepProps {
  username: string;
  onUsernameChange: (value: string) => void;
  onRotate: () => void;
  /** When false the name is generated, and the field is read-only. */
  canCustomize: boolean;
  disabled?: boolean;
  rejection: NameCheckRejectionCode | null;
  /** The check could not be completed (offline, rate limited, expired token). */
  checkFailed: boolean;
}

/** Maps a server verdict to its i18n key. */
export function usernameRejectionKey(code: NameCheckRejectionCode): string {
  switch (code) {
    case 'TAKEN':
      return 'identity.create.errorUsernameTaken';
    case 'BLOCKED':
      return 'identity.create.errorUsernameBlocked';
    case 'TOO_SHORT':
      return 'identity.create.errorUsernameTooShort';
    case 'TOO_LONG':
      return 'identity.create.errorUsernameTooLong';
    case 'INVALID_CHARS':
      return 'identity.create.errorUsernameInvalidChars';
    case 'NOT_GENERATED':
      return 'identity.create.errorUsernameNotGenerated';
  }
}

export function CreateUsernameStep({
  username,
  onUsernameChange,
  onRotate,
  canCustomize,
  disabled,
  rejection,
  checkFailed,
}: CreateUsernameStepProps) {
  const { t } = useTranslation();

  return (
    <div className="identity-wizard-step">
      <Input
        label={t('identity.create.usernameLabel')}
        type="text"
        placeholder={t('identity.create.usernamePlaceholder')}
        value={username}
        onChange={(e) => onUsernameChange(e.target.value)}
        disabled={disabled}
        readOnly={!canCustomize}
        maxLength={USERNAME_MAX_LENGTH}
        autoFocus={canCustomize}
        autoComplete="off"
        spellCheck={false}
        hint={t('identity.create.usernameHintUnique')}
        error={
          checkFailed
            ? t('identity.create.errorCheckFailed')
            : rejection
              ? t(usernameRejectionKey(rejection))
              : undefined
        }
        rightIcon={
          canCustomize ? undefined : (
            <Tooltip content={t('identity.create.usernameRotate')}>
              <button
                type="button"
                className="identity-username-rotate"
                onClick={onRotate}
                disabled={disabled}
                aria-label={t('identity.create.usernameRotate')}
              >
                <Icon name="rotateRight" />
              </button>
            </Tooltip>
          )
        }
      />

      <p className="form-hint">
        {canCustomize
          ? t('identity.create.usernameNotePaid')
          : t('identity.create.usernameNoteFree')}
      </p>
    </div>
  );
}
